import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/lib/auth-context";

/**
 * Route guard for pages that require an authenticated user. Wrap a route element:
 * `<Route element={<RequireAuth><Dashboard /></RequireAuth>} />`.
 *
 * Behaviour:
 * - While the persisted session is still being restored (`loading`), renders nothing —
 *   so a hard refresh of a gated page doesn't flash the login redirect before the token
 *   is restored.
 * - Once resolved, an unauthenticated visitor is redirected to `/login?next=<here>`
 *   (`replace`, so the gated URL isn't left in history). The login/register flow reads
 *   `next` and returns them here via `safeNext` after they sign in.
 *
 * Use for internal app routes only (dashboard, settings, upload, suggest, suggestion
 * detail). Public resource pages that merely *may* need auth — e.g. a shared skill link —
 * stay ungated and fall back to {@link AuthGate} on load failure, so a newcomer following
 * the link gets context (and a Sign-up option) rather than a bare login form.
 */
export default function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;
  if (!user) {
    const next = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}
